import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import {
  useMyLocations,
  branchLabel,
  locationsWithRole,
  MyLocation,
} from '../hooks/useMyLocations';

interface Props {
  value: string | string[];
  onChange: (v: any) => void;
  multiple?: boolean;
  label?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  /** Restrict the options, e.g. to the branches a person already works at. */
  only?: string[];
  helperText?: string;
  /**
   * Only offer locations where this account holds at least this role
   * ('owner' = studio_owner / brand_owner, 'manager' = manager or above).
   * Not applied when the server did not send `roleByStudio`.
   */
  minRole?: 'owner' | 'manager';
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHICH LOCATION IS THIS FOR
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * One control, because the answer has to be asked the same way everywhere.
 *
 * A brand owner holds several locations, and while they are viewing "All" a
 * new class, session or instructor has no implied home. The old behaviour was
 * to quietly use whichever branch they happened to sign in as — the worst
 * possible default, because the record looks right until someone notices it is
 * at the wrong address. The server now refuses those writes outright, so the
 * question has to be asked in the UI.
 *
 * Deliberately starts EMPTY rather than pre-selecting the first location:
 * a pre-filled answer to a question nobody read is the same silent default in
 * a new costume.
 */
const BranchSelect: React.FC<Props> = ({
  value,
  onChange,
  multiple = false,
  label = 'Location',
  required = true,
  size = 'small',
  fullWidth = true,
  only,
  minRole,
}) => {
  const { locations, roleByStudio } = useMyLocations();
  const allowed = minRole ? locationsWithRole(roleByStudio, minRole) : null;
  const options: MyLocation[] = locations.filter(
    (l) =>
      (!only?.length || only.includes(l._id)) &&
      (!allowed || allowed.includes(String(l._id))),
  );

  const labelFor = (id: string) =>
    branchLabel(options.find((l) => l._id === id)) || id;

  return (
    <FormControl fullWidth={fullWidth} size={size} required={required}>
      <InputLabel>{required ? `${label} *` : label}</InputLabel>
      <Select
        multiple={multiple}
        value={value as any}
        label={required ? `${label} *` : label}
        onChange={(e) => onChange(e.target.value)}
        input={<OutlinedInput label={required ? `${label} *` : label} />}
        renderValue={(selected: any) =>
          multiple ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((id) => (
                <Chip
                  key={id}
                  size="small"
                  icon={<LocationOn sx={{ fontSize: 14 }} />}
                  label={labelFor(id)}
                  sx={{ height: 22, fontSize: '0.72rem' }}
                />
              ))}
            </Box>
          ) : (
            labelFor(selected as string)
          )
        }
      >
        {options.map((l) => (
          <MenuItem key={l._id} value={l._id}>
            {multiple && (
              <Checkbox
                size="small"
                checked={(value as string[])?.includes?.(l._id) ?? false}
              />
            )}
            <ListItemText
              primary={branchLabel(l)}
              secondary={l.studioName !== branchLabel(l) ? l.studioName : undefined}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default BranchSelect;
